<template>
  <div class="filter-builder">
    <div
      :data-testid="`${testIdPrefix}__filter-top-bar`"
      class="filter-builder__top-bar"
    >
      <div class="filter-builder__grouping-logic">
        <span class="filter-builder__grouping-logic-label">
          Grouping Logic: Match <strong>{{ groupingConnector === 'AND' ? 'ALL' : 'ANY' }}</strong> of the filter groups
        </span>
        <div class="filter-builder__logic-toggle">
          <button
            type="button"
            :data-testid="`${testIdPrefix}__grouping-logic-and`"
            :class="['filter-builder__logic-btn', groupingConnector === 'AND' ? 'filter-builder__logic-btn--active' : '']"
            @click="$emit('set-grouping-connector', 'AND')"
          >
            AND
          </button>
          <button
            type="button"
            :data-testid="`${testIdPrefix}__grouping-logic-or`"
            :class="['filter-builder__logic-btn', groupingConnector === 'OR' ? 'filter-builder__logic-btn--active' : '']"
            @click="$emit('set-grouping-connector', 'OR')"
          >
            OR
          </button>
        </div>
      </div>
      <button
        type="button"
        :data-testid="`${testIdPrefix}__add-group-btn-top`"
        class="filter-builder__add-group-btn-compact"
        @click="$emit('add-group')"
      >
        <svg class="filter-builder__add-group-icon" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24">
          <path d="M12 5v14M5 12h14" />
        </svg>
        Add Filter Group
      </button>
    </div>

    <div v-for="(group, groupIdx) in filterGroups" :key="group.id">
      <div
        :data-testid="`${testIdPrefix}__filter-group`"
        class="filter-builder__group"
      >
        <div class="filter-builder__group-header">
          <div class="filter-builder__group-header-left">
            <span class="filter-builder__group-label">Filter Set {{ groupIdx + 1 }}</span>
            <input
              type="text"
              :data-testid="`${testIdPrefix}__group-name-input`"
              :value="group.name"
              placeholder="Enter a friendly name for this rule set"
              class="filter-builder__group-name-input"
              @input="$emit('update-group-name', group.id, $event.target.value)"
            />
          </div>
          <button
            type="button"
            :data-testid="`${testIdPrefix}__remove-group-btn`"
            class="filter-builder__group-remove-btn"
            :disabled="filterGroups.length === 1"
            :title="filterGroups.length === 1 ? 'Cannot remove the last filter group' : 'Remove this filter group'"
            @click="$emit('remove-group', group.id)"
          >
            <v-icon class="filter-builder__group-remove-icon" color="error">mdi-delete</v-icon>
          </button>
        </div>

        <div class="filter-builder__group-logic">
          <span class="filter-builder__group-logic-label">
            Conditions Logic: Match <strong>{{ group.connector === 'AND' ? 'ALL' : 'ANY' }}</strong> of the conditions below
          </span>
          <div class="filter-builder__logic-toggle">
            <button
              type="button"
              :data-testid="`${testIdPrefix}__group-logic-and`"
              :class="['filter-builder__logic-btn', group.connector === 'AND' ? 'filter-builder__logic-btn--active' : '']"
              @click="$emit('update-group-connector', group.id, 'AND')"
            >
              AND
            </button>
            <button
              type="button"
              :data-testid="`${testIdPrefix}__group-logic-or`"
              :class="['filter-builder__logic-btn', group.connector === 'OR' ? 'filter-builder__logic-btn--active' : '']"
              @click="$emit('update-group-connector', group.id, 'OR')"
            >
              OR
            </button>
          </div>
        </div>

        <div class="filter-builder__conditions">
          <div
            v-for="(condition, condIdx) in group.conditions"
            :key="condition.id"
            :data-testid="`${testIdPrefix}__condition`"
            class="filter-builder__condition"
          >
            <span class="filter-builder__condition-number">{{ condIdx + 1 }}</span>
            <lb-dropdown
              :data-testid="`${testIdPrefix}__condition-column`"
              :value="condition.column"
              :items="filterColumns"
              itemtext="label"
              itemvalue="value"
              label=""
              hidedetails
              :clearable="false"
              class="filter-builder__condition-select"
              @change="$emit('update-condition', group.id, condition.id, 'column', $event)"
            />
            <lb-dropdown
              :data-testid="`${testIdPrefix}__condition-operator`"
              :value="condition.operator"
              :items="filterOperators"
              itemtext="label"
              itemvalue="value"
              label=""
              hidedetails
              :clearable="false"
              class="filter-builder__condition-select filter-builder__condition-select--operator"
              @change="$emit('update-condition', group.id, condition.id, 'operator', $event)"
            />
            <input
              v-if="operatorRequiresValue(condition.operator)"
              type="text"
              :data-testid="`${testIdPrefix}__condition-value`"
              :value="condition.value"
              placeholder="Enter value"
              class="filter-builder__condition-input noTransform"
              @input="$emit('update-condition', group.id, condition.id, 'value', $event.target.value)"
            />
            <span v-else class="filter-builder__condition-null-label">(no value required)</span>
            <button
              type="button"
              :data-testid="`${testIdPrefix}__remove-condition-btn`"
              class="filter-builder__condition-remove-btn"
              title="Remove this condition"
              @click="$emit('remove-condition', group.id, condition.id)"
            >
              <v-icon class="filter-builder__condition-remove-icon" color="error">mdi-delete</v-icon>
            </button>
          </div>
        </div>

        <button
          type="button"
          :data-testid="`${testIdPrefix}__add-condition-btn`"
          class="filter-builder__add-condition-btn"
          @click="$emit('add-condition', group.id)"
        >
          <svg class="filter-builder__add-icon" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Add Condition
        </button>
      </div>
    </div>

    <div class="filter-builder__actions">
      <button
        type="button"
        :data-testid="`${testIdPrefix}__reset-filters-btn`"
        class="filter-builder__reset-btn"
        @click="$emit('reset')"
      >
        Reset
      </button>
      <button
        type="button"
        :data-testid="`${testIdPrefix}__apply-filters-btn`"
        class="filter-builder__apply-btn"
        :disabled="!canApply"
        @click="$emit('apply')"
      >
        Apply
      </button>
    </div>
  </div>
</template>

<script>
import { operatorRequiresValue } from './tmResultsDataTableUtils.js';

export default {
  name: 'TMResultsFilterBuilder',

  props: {
    testIdPrefix: {
      type: String,
      required: true,
    },
    filterGroups: {
      type: Array,
      default: () => [],
    },
    groupingConnector: {
      type: String,
      default: 'OR',
    },
    filterColumns: {
      type: Array,
      default: () => [],
    },
    filterOperators: {
      type: Array,
      default: () => [],
    },
    /**
     * True when filters are currently applied to the table.
     * Lets the user Apply after deleting the last condition to clear them.
     */
    hasActiveFilters: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    canApply() {
      const hasCompleteCondition = (this.filterGroups || []).some((group) =>
        (group.conditions || []).some((condition) => {
          if (!condition.column || !condition.operator) return false;
          return !operatorRequiresValue(condition.operator) || this.hasFilterValue(condition);
        })
      );
      // Empty/default rows disable Apply unless the user is clearing applied filters.
      return hasCompleteCondition || this.hasActiveFilters;
    },
  },

  methods: {
    operatorRequiresValue,
    hasFilterValue(condition) {
      if (condition.value === null || condition.value === undefined) return false;
      if (typeof condition.value === 'number') return !Number.isNaN(condition.value);
      return String(condition.value).trim() !== '';
    },
  },
};
</script>
